import { useEffect, useState } from 'react';
import {
  fetchSubredditWhitelist,
  setSubredditWhitelist,
  type RelayConfig,
} from '../api/client';

interface Props {
  cfg: RelayConfig;
}

/**
 * Operator settings page. Currently only the subreddit whitelist —
 * a single global pool the warming orchestrator samples 3-5 subs from
 * to seed each new account's `target_subreddits` on its first warm.
 *
 * Persisted in Worker KV under `settings:subreddit_whitelist`, so every
 * consumer machine reads the same snapshot — no per-host sync needed.
 */
export function Settings({ cfg }: Props) {
  const [list, setList] = useState<string[] | null>(null);
  const [draft, setDraft] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [toast, setToast] = useState<string | null>(null);

  useEffect(() => {
    refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cfg.url, cfg.token]);

  async function refresh() {
    setErr(null);
    try {
      const subs = await fetchSubredditWhitelist(cfg);
      setList(subs);
      setDraft(subs.join('\n'));
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
    }
  }

  async function save() {
    setBusy(true);
    setErr(null);
    try {
      const parsed = parseSubredditList(draft);
      const r = await setSubredditWhitelist(cfg, parsed);
      setToast(`Saved ${r.count} subreddit(s)`);
      await refresh();
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
      setTimeout(() => setToast(null), 3000);
    }
  }

  return (
    <div className="mx-auto max-w-3xl space-y-6">
      <header>
        <h1 className="text-xl font-semibold tracking-tight">Settings</h1>
        <p className="mt-1 text-sm text-[var(--color-text-dim)]">
          Global pool the warming orchestrator samples from when an account
          has no per-account <code>target_subreddits</code>. Once seeded,
          the per-account list is fixed; future warms stay inside it.
        </p>
      </header>

      {toast && (
        <div className="rounded-md border border-emerald-500/30 bg-emerald-500/10 px-3 py-2 text-sm text-emerald-200">
          {toast}
        </div>
      )}
      {err && (
        <div className="rounded-md border border-rose-500/30 bg-rose-500/10 px-3 py-2 text-sm text-rose-200">
          {err}
        </div>
      )}

      <section className="card overflow-hidden">
        <div className="flex items-center justify-between border-b border-[var(--color-border)] px-4 py-3">
          <span className="text-xs font-medium uppercase tracking-wide text-[var(--color-text-faint)]">
            Subreddit whitelist
          </span>
          <span className="text-xs text-[var(--color-text-faint)]">
            {list ? `${list.length} subreddit(s)` : 'loading…'}
          </span>
        </div>
        <div className="space-y-3 p-4">
          <p className="text-xs text-[var(--color-text-dim)]">
            One per line, or comma-separated. <code>r/</code> prefix is
            tolerated. Server normalizes to lowercase and dedupes — refresh
            after saving to see the canonical form.
          </p>
          <textarea
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            rows={14}
            spellCheck={false}
            placeholder={'python\nhiking\nmachinelearning\n…'}
            className="w-full rounded-md border border-[var(--color-border-strong)] bg-[var(--color-bg)] p-3 font-mono text-xs text-zinc-200 placeholder:text-[var(--color-text-faint)] focus:border-zinc-600 focus:outline-none"
          />
          <div className="flex justify-end">
            <button onClick={save} disabled={busy} className="btn-primary">
              {busy ? 'Saving…' : 'Save'}
            </button>
          </div>
        </div>
      </section>

      {list && list.length > 0 && (
        <section className="card overflow-hidden">
          <div className="flex items-center justify-between border-b border-[var(--color-border)] px-4 py-3">
            <span className="text-xs font-medium uppercase tracking-wide text-[var(--color-text-faint)]">
              Currently stored
            </span>
          </div>
          <div className="flex flex-wrap gap-1.5 p-4 text-xs">
            {list.map((sub) => (
              <span
                key={sub}
                className="rounded-md border border-[var(--color-border)] bg-[var(--color-surface-2)] px-2 py-0.5 text-[var(--color-text-dim)]"
              >
                r/{sub}
              </span>
            ))}
          </div>
        </section>
      )}
    </div>
  );
}

/** Parse textarea content into a clean subreddit list (newline OR comma
 * separated). Drops empties; the Worker handles canonical dedup/lowercase. */
function parseSubredditList(raw: string): string[] {
  const out: string[] = [];
  for (const piece of raw.split(/[,\s]+/)) {
    const s = piece.trim().replace(/^\/?r\//i, '');
    if (s) out.push(s);
  }
  return out;
}
